import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { completeAuthorization } from "@/services/mcpOAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");
    const oauthErrorDesc = params.get("error_description");

    if (oauthError) {
      setError(oauthErrorDesc || oauthError);
      return;
    }
    if (!code || !state) {
      setError("Missing authorization code or state in callback URL.");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { returnTo } = await completeAuthorization(code, state);
        if (cancelled) return;
        toast.success("Signed in");
        navigate(returnTo, { replace: true });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Sign-in failed";
        setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8 bg-card border rounded-lg shadow-sm text-center space-y-4">
        {!error ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
            <h1 className="text-lg font-semibold text-foreground">Completing sign-in…</h1>
            <p className="text-sm text-muted-foreground">
              Exchanging your authorization code for a session token.
            </p>
          </>
        ) : (
          <>
            <h1 className="text-lg font-semibold text-foreground">Sign-in failed</h1>
            <Alert variant="destructive">
              <AlertDescription className="text-left break-words">{error}</AlertDescription>
            </Alert>
            <Button onClick={() => navigate("/login", { replace: true })} className="w-full">
              Back to login
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
